import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { CheckCircle } from 'lucide-react';
import { jiraService } from '@/services/jiraService';
import { supabase } from '@/integrations/supabase/client';

const Support = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [formData, setFormData] = useState({
    issueType: '',
    email: '',
    summary: '',
    description: ''
  });

  // Load user email if user is logged in
  useEffect(() => {
    const fetchUserEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setUserEmail(session.user.email);
        setFormData(prev => ({ ...prev, email: session.user.email }));
      }
    };
    
    fetchUserEmail();
  }, []);

  // Reset success state after 5 seconds
  useEffect(() => {
    let timer;
    if (isSuccess) {
      timer = setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [isSuccess]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      issueType: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.issueType || !formData.email || !formData.summary || !formData.description) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Map our form's issue type values to Jira types
      const jiraTypeMap = {
        'bug': 'Bug',
        'feature': 'Story',
        'question': 'Task',
        'other': 'Task'
      };
      
      // Format description to include email for reply
      const formattedDescription = `
Submitted by: ${formData.email}

${formData.description}
      `;
      
      // Create a ticket in Jira
      await jiraService.createTicket({
        summary: formData.summary,
        description: formattedDescription,
        type: jiraTypeMap[formData.issueType]
      });
      
      // Show success in UI
      setIsSuccess(true);
      
      toast({
        title: "Support Ticket Created",
        description: "We've received your ticket and will respond to your email shortly.",
      });
      
      // Reset form
      setFormData({
        issueType: '',
        email: userEmail || '',
        summary: '',
        description: ''
      });
    } catch (error) {
      console.error("Error creating Jira ticket:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create support ticket. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-6 py-6">
      <PageHeader 
        title="Support" 
        description="Get help with Coach Vault"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Create Support Ticket</CardTitle>
          </CardHeader>
          <CardContent>
            {isSuccess ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-6 flex flex-col items-center text-center">
                <CheckCircle className="text-green-500 w-12 h-12 mb-4" />
                <h3 className="text-xl font-semibold text-green-800 mb-2">Ticket Submitted Successfully!</h3>
                <p className="text-green-700 mb-6">Thank you for your feedback. We'll respond to your email shortly.</p>
                <Button onClick={() => setIsSuccess(false)} variant="outline" className="bg-white text-green-700 border-green-300 hover:bg-green-50 hover:text-green-800">
                  Submit Another Ticket
                </Button>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="issue-type">Issue Type*</Label>
                    <Select value={formData.issueType} onValueChange={handleSelectChange}>
                      <SelectTrigger id="issue-type" className="bg-background border-input text-foreground">
                        <SelectValue placeholder="Select an issue type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bug">Bug</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                        <SelectItem value="question">Question</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Your Email*</Label>
                    <Input 
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="summary">Summary*</Label>
                  <Input 
                    id="summary"
                    placeholder="Brief summary of the issue"
                    value={formData.summary}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description*</Label>
                  <Textarea 
                    id="description"
                    placeholder="Detailed description of the issue"
                    className="min-h-[200px]"
                    value={formData.description}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="pt-4">
                  <Button type="submit" className="w-full md:w-auto" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Create Support Ticket"}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Support Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-1">Support Hours</h3>
                <p className="text-sm text-muted-foreground">Monday to Friday, 9am to 5pm GMT</p>
              </div>
              
              <div>
                <h3 className="font-medium mb-1">Email Support</h3>
                <p className="text-sm text-muted-foreground">support@coachvault.com</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Support;
